import {
  ContentSwitcher,
  type DropdownItem,
  Field,
  IconButton,
  MultiSelectDropdown,
  SingleSelectDropdown,
  TextInput,
} from "@carbon6/c6-frontend-design";
import { useCallback, useState, type ChangeEvent, type ReactElement, type ReactNode } from "react";
import type { ReservedKeyContract } from "@/api";
import { hatcheryDropdownModes } from "@/app/theme";
import {
  indexContractsByKey,
  RESERVED_KEY_UI,
  reservedKeyLabel,
  reservedValueOptions,
  type ReservedSelections,
} from "@/components/label";
import styles from "./ReservedLabelForm.module.css";

export type ReservedLabelFormProps = {
  contracts: readonly ReservedKeyContract[];
  /** reserved key 들 (버킷). 이 순서대로 필드를 렌더한다. */
  keys: readonly string[];
  selections: ReservedSelections;
  onSelectionsChange: (next: ReservedSelections) => void;
  /** 필수 입력으로 표시할 key 들. Field 라벨에 `*` 마커를 붙이고 컨트롤에 required 를 넘긴다. */
  requiredKeys?: readonly string[];
  /** 그리드 열 수(기본 2). 예약 라벨을 균등 열로 둘 때 쓴다. */
  columns?: number;
  /**
   * 명시 grid-template-columns(예: "minmax(0,1fr) 11rem"으로 앞 컨트롤이 남은 폭을 차지한다).
   * columns 보다 우선한다.
   */
  gridColumns?: string;
  /** 예약 라벨과 같은 행에 이어 붙일 일반 셀(예: 행동 주체 드롭다운). 라벨 축이 아니라
   * 같은 줄에 놓이는 별도 컨트롤이다. 그래서 selections 에는 영향이 없다. */
  trailingCell?: ReactNode;
  /** 예약 라벨 아래 전체 폭 행으로 렌더할 추가 셀(예: embodiment 선택 버튼). */
  extraCell?: ReactNode;
  disabled?: boolean;
  className?: string;
};

export function ReservedLabelForm({
  contracts,
  keys,
  selections,
  onSelectionsChange,
  requiredKeys,
  columns,
  gridColumns,
  trailingCell,
  extraCell,
  disabled = false,
  className,
}: ReservedLabelFormProps): ReactNode {
  const contractByKey = indexContractsByKey(contracts);
  const requiredKeySet = new Set(requiredKeys ?? []);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const setKeyValues = useCallback(
    (key: string, values: readonly string[]) => {
      onSelectionsChange({ ...selections, [key]: values });
    },
    [onSelectionsChange, selections],
  );

  const addOpenValue = useCallback(
    (key: string) => {
      const draft = (drafts[key] ?? "").trim();
      if (draft === "") {
        return;
      }
      const current = selections[key] ?? [];
      if (!current.includes(draft)) {
        onSelectionsChange({ ...selections, [key]: [...current, draft] });
      }
      setDrafts((previous) => ({ ...previous, [key]: "" }));
    },
    [drafts, onSelectionsChange, selections],
  );

  const gridStyle =
    gridColumns !== undefined
      ? { gridTemplateColumns: gridColumns }
      : columns !== undefined
        ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
        : undefined;

  return (
    <div className={[styles.form, className ?? ""].filter(Boolean).join(" ")} style={gridStyle}>
      {keys.map((key) => {
        const ui = RESERVED_KEY_UI[key];
        if (ui === undefined) {
          return null;
        }
        const required = requiredKeySet.has(key);
        const control = renderControl({
          key,
          contract: contractByKey.get(key),
          selected: selections[key] ?? [],
          disabled,
          required,
          draft: drafts[key] ?? "",
          onValuesChange: (values) => setKeyValues(key, values),
          onDraftChange: (value) => setDrafts((previous) => ({ ...previous, [key]: value })),
          onAddOpenValue: () => addOpenValue(key),
        });
        if (control === null) {
          return null;
        }
        // task(openMultiSelect)는 입력 행이 있는 합성 컨트롤이다. 그래서 한 행을 모두 차지한다.
        const cellClassName = [styles.cell, ui.control === "openMultiSelect" ? styles.cellWide : ""]
          .filter(Boolean)
          .join(" ");
        return (
          <div key={key} className={cellClassName}>
            <Field
              // 필수 표시는 같은 행의 다른 필수 필드(데이터셋 이름·행동 주체)와 같은 `*` 마커로 맞춘다.
              label={
                required ? (
                  <>
                    {ui.label} <span className={styles.requiredMark}>*</span>
                  </>
                ) : (
                  ui.label
                )
              }
              layout="stack"
              disabled={disabled}
            >
              {control}
            </Field>
          </div>
        );
      })}
      {trailingCell !== undefined ? <div className={styles.cell}>{trailingCell}</div> : null}
      {extraCell !== undefined ? <div className={styles.cellWide}>{extraCell}</div> : null}
    </div>
  );
}

type RenderControlArgs = {
  key: string;
  contract: ReservedKeyContract | undefined;
  selected: readonly string[];
  disabled: boolean;
  required: boolean;
  draft: string;
  onValuesChange: (values: readonly string[]) => void;
  onDraftChange: (value: string) => void;
  onAddOpenValue: () => void;
};

function renderControl(args: RenderControlArgs): ReactElement | null {
  const { key, contract, selected, disabled, required } = args;
  const ui = RESERVED_KEY_UI[key];
  const label = reservedKeyLabel(key);
  // 빈 상태는 요청 문구 대신 현재 상태만 적는다. 값 이름은 Field 라벨이 이미 표시한다.
  const placeholder = "선택 안 함";

  switch (ui?.control) {
    case "singleDropdown":
      return (
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label={label}
          // Field 가 제목을 이미 보여준다. 그래서 트리거에는 값만 남긴다(접근성 이름은
          // `label: value` 를 유지한다).
          fullWidth
          placeholder={placeholder}
          options={reservedValueOptions(key, contract?.values)}
          value={selected[0] ?? null}
          required={required}
          disabled={disabled}
          onValueChange={(value) => args.onValuesChange([value])}
        />
      );

    case "switcher":
      return (
        <ContentSwitcher
          label={label}
          // 셀을 가득 채운다. 높이는 md(공용 controlHeight)로 두어 이웃 드롭다운(md 고정)과 정렬한다.
          width={{ type: "expanded" }}
          size="md"
          options={reservedValueOptions(key, contract?.values)}
          value={selected[0] ?? null}
          disabled={disabled}
          onValueChange={(value) => args.onValuesChange([value])}
        />
      );

    case "openMultiSelect":
      return (
        <div className={styles.openGroup}>
          <MultiSelectDropdown
            mode={hatcheryDropdownModes.default}
            label={label}
            placeholder={placeholder}
            options={openOptions(selected)}
            selectedValues={selected}
            closeOnSelect={false}
            required={required}
            disabled={disabled}
            emptyMessage="선택할 값이 없습니다. 아래에서 새 값을 추가해 주세요."
            onSelectionChange={args.onValuesChange}
          />
          <div className={styles.openRow}>
            <TextInput
              className={styles.openInput}
              value={args.draft}
              placeholder={`${label} 값을 입력해 주세요.`}
              disabled={disabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                args.onDraftChange(event.target.value)
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  args.onAddOpenValue();
                }
              }}
            />
            <IconButton
              text="추가"
              type="button"
              variant="surface"
              size="sm"
              disabled={disabled || args.draft.trim() === ""}
              onClick={args.onAddOpenValue}
            />
          </div>
        </div>
      );

    default:
      return null;
  }
}

function openOptions(selected: readonly string[]): DropdownItem[] {
  return Array.from(new Set(selected)).map((value) => ({ value, label: value }));
}
